import { toast } from "react-hot-toast";
import {
  Form,
  LoaderFunctionArgs,
  ActionFunctionArgs,
  useLoaderData,
  redirect,
  useFetcher,
  useNavigate,
} from "react-router-dom";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input, TextArea } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { ShowLayout } from "../../layout/ShowLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries";
import { organizationStore } from "../../store/organization";
import { Attendee } from "../../types/attendee";
import { Event } from "../../types/event";
import { UPDATE_ATTENDEE } from "./mutations";
import {
  GET_ATTENDEE,
  GET_ATTENDEE_CARD,
  GET_ATTENDEE_ORDERS,
  GET_ATTENDEE_ORDERS_BY_ATTENDEE,
  GET_ATTENDEE_RFID,
  GET_ATTENDEE_PROMO_CODES,
  GET_ATTENDEE_TICKETING_ORDERS,
  GET_ATTENDEE_TICKETING_ORDERS_BY_ATTENDEE,
} from "./queries";
import DeleteModal from "../../components/DeleteModal";
import { SecondaryButton, TertiaryButton } from "../../components/Button";
import { usePermissions } from "../auth/api";
import React, { useEffect, useRef, useState } from "react";
import { GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { rfidColDefs } from "./components/rfidAssetsHelpers";
import { cardsOnFileColDefs } from "./components/cardsOnFileHelpers";
import { promoCodesColDefs } from "./components/promoCodesHelpers";
import {
  CardsOnFileRow,
  RFIDAssetsRow,
  TransactionReportRow,
} from "./components/type";
import Loader from "../../components/Loader";
import { ordersColDefs } from "./components/ordersHelpers";
import { ordersColDefs as ticketingOrdersColDefs } from "../tickets/components/ordersHelpers";
import { userStore } from "../../store/user";
import { PromoCode } from "../../types/promoCode";
import { DemographicAnswer } from "../../types/attendee";

const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const attendeeEditLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getState().organizationId;
  const user = getUserStore().user;

  let where: any = {
    organization_id: { _eq: organizationId },
  };
  if (user?.events) {
    where = {
      ...where,
      id: {
        _in: user?.events,
      },
    };
  }
  const [{ data: attendeeData }, { data: eventsData }] = await Promise.all([
    client.query({
      query: GET_ATTENDEE,
      variables: {
        id: params?.id,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where,
      },
    }),
  ]);
  return {
    attendee: attendeeData.attendees_by_pk,
    events: eventsData.events,
  };
};

export const attendeeEditAction = async ({
                                           params,
                                           request,
                                         }: ActionFunctionArgs) => {
  const { id } = params;
  const body = await request.formData();
  const bodyData = Object.fromEntries(body);
  try {
    await client.mutate({
      mutation: UPDATE_ATTENDEE,
      variables: {
        input: bodyData,
        where: {
          id: { _eq: id },
        },
      },
    });
    toast.success("Attendee updated successfully");
    return redirect(`/attendees/list`);
  } catch (error) {
    toast.error("Error updating attendee");
  }
};

export const AttendeeEdit = () => {
  const rfidGridRef = useRef<GridApi>();
  const cardGridRef = useRef<GridApi>();
  const orderGridRef = useRef<GridApi>();
  const ticketingOrderGridRef = useRef<GridApi>();
  const promoCodeGridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  const [isLoadingRFID, setIsLoadingRFID] = useState(true);
  const [isLoadingCard, setIsLoadingCard] = useState(true);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [isLoadingTicketingOrder, setIsLoadingTicketingOrder] = useState(true);
  const [isLoadingPromoCodes, setIsLoadingPromoCodes] = useState(true);
  const { attendee, events } = useLoaderData() as {
    attendee: Attendee;
    events: Event[];
  };
  const [orderRowData, setOrderRowData] = useState<TransactionReportRow[]>([]);
  const [ticketingOrderRowData, setTicketingOrderRowData] = useState<TransactionReportRow[]>([]);
  const [cardRowData, setCardRowData] = useState<CardsOnFileRow[]>([]);
  const [rfidRowData, setRfidRowData] = useState<RFIDAssetsRow[]>([]);
  const [promoCodeRowData, setPromoCodeRowData] = useState<PromoCode[]>([]);
  const [attendeeOrderData, setAttendeeOrderData] = useState<
      TransactionReportRow[]
  >([]);
  const [rfidOrderData, setRfidOrderData] = useState<TransactionReportRow[]>(
      []
  );
  const [attendeeTicketingOrderData, setAttendeeTicketingOrderData] = useState<
      TransactionReportRow[]
  >([]);
  const [rfidTicketingOrderData, setRfidTicketingOrderData] = useState<TransactionReportRow[]>(
      []
  );
  const fetcher = useFetcher();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rfidData] = await Promise.all([
          client.query({
            query: GET_ATTENDEE_RFID,
            variables: {
              id: attendee?.id,
              event_id: attendee?.event_id,
            },
          }),
        ]);
        setRfidRowData(rfidData.data.rfid_assets);
      } catch (error) {
        toast.error("Error Fetching RFID Data");
      } finally {
        setIsLoadingRFID(false);
      }
    };
    fetchData();
  }, [attendee]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardData] = await Promise.all([
          client.query({
            query: GET_ATTENDEE_CARD,
            variables: {
              id: attendee?.id,
              event_id: attendee?.event_id,
            },
          }),
        ]);
        setCardRowData(cardData.data.card_on_files);
      } catch (error) {
        toast.error("Error Fetching Card Data");
      } finally {
        setIsLoadingCard(false);
      }
    };
    fetchData();
  }, [attendee]);

  // Fetch orders based on rfidRowData
  useEffect(() => {
    const fetchRfidOrders = async (uids: string[]) => {
      try {
        const orderData = await client.query({
          query: GET_ATTENDEE_ORDERS,
          variables: {
            uids,
            event_id: attendee?.event_id,
          },
        });
        setRfidOrderData(orderData.data.reports_transactions);
      } catch (error) {
        toast.error("Error Fetching RFID Order Data: " + error);
      }
    };

    if (rfidRowData.length > 0) {
      const uids = rfidRowData.map((row) => row.uid);
      fetchRfidOrders(uids);
    }
  }, [rfidRowData, attendee?.event_id]);

  // Fetch orders based on attendee data
  useEffect(() => {
    const fetchAttendeeOrders = async () => {
      try {
        const orderData2 = await client.query({
          query: GET_ATTENDEE_ORDERS_BY_ATTENDEE,
          variables: {
            attendeeId: attendee?.id,
            event_id: attendee?.event_id,
          },
        });
        setAttendeeOrderData(orderData2.data.reports_transactions ?? []);
      } catch (error) {
        toast.error("Error Fetching Attendee Order Data: " + error);
      }
    };

    if (attendee?.id && attendee?.event_id) {
      fetchAttendeeOrders();
    }
  }, [attendee]);

  // Combine the two datasets
  useEffect(() => {
    if (attendeeOrderData.length > 0 || rfidOrderData.length > 0) {
      const combinedOrderData = [
        ...attendeeOrderData,
        ...rfidOrderData,
      ];

      const uniqueOrders = Array.from(
          new Set(combinedOrderData.map(order => order.order_id))
      ).map(order_id =>
          combinedOrderData.find(order => order.order_id === order_id)
      ).filter(order => order !== undefined);

      setOrderRowData(uniqueOrders as TransactionReportRow[]);
      setIsLoadingOrder(false);
    } else {
      setOrderRowData([]);
      setIsLoadingOrder(false);
    }
  }, [attendeeOrderData, rfidOrderData]);

  // Fetch promo codes
  useEffect(() => {
    const fetchPromoCodes = async () => {
      try {
        const promoCodeData = await client.query({
          query: GET_ATTENDEE_PROMO_CODES,
          variables: {
            attendeeId: attendee?.id,
          },
        });
        setPromoCodeRowData(promoCodeData.data.promo_code ?? []);
      } catch (error) {
        toast.error("Error Fetching Promo Codes: " + error);
      } finally {
        setIsLoadingPromoCodes(false);
      }
    };

    if (attendee?.id) {
      fetchPromoCodes();
    }
  }, [attendee]);

  // Fetch ticketing orders based on rfidRowData
  useEffect(() => {
    const fetchRfidTicketingOrders = async (uids: string[]) => {
      try {
        const orderData = await client.query({
          query: GET_ATTENDEE_TICKETING_ORDERS,
          variables: {
            uids,
            event_id: attendee?.event_id,
          },
        });
        setRfidTicketingOrderData(orderData.data.reports_transactions_ticketing);
      } catch (error) {
        toast.error("Error Fetching RFID Ticketing Order Data: " + error);
      }
    };

    if (rfidRowData.length > 0) {
      const uids = rfidRowData.map((row) => row.uid);
      fetchRfidTicketingOrders(uids);
    }
  }, [rfidRowData, attendee?.event_id]);

  // Fetch ticketing orders based on attendee data
  useEffect(() => {
    const fetchAttendeeTicketingOrders = async () => {
      try {
        const orderData2 = await client.query({
          query: GET_ATTENDEE_TICKETING_ORDERS_BY_ATTENDEE,
          variables: {
            attendeeId: attendee?.id,
            event_id: attendee?.event_id,
          },
        });
        setAttendeeTicketingOrderData(orderData2.data.reports_transactions_ticketing ?? []);
      } catch (error) {
        toast.error("Error Fetching Attendee Ticketing Order Data: " + error);
      }
    };

    if (attendee?.id && attendee?.event_id) {
      fetchAttendeeTicketingOrders();
    }
  }, [attendee]);

  // Combine the two ticketing order datasets
  useEffect(() => {
    if (attendeeTicketingOrderData.length > 0 || rfidTicketingOrderData.length > 0) {
      const combinedOrderData = [
        ...attendeeTicketingOrderData,
        ...rfidTicketingOrderData,
      ];

      const uniqueOrders = Array.from(
          new Set(combinedOrderData.map(order => order.order_id))
      ).map(order_id =>
          combinedOrderData.find(order => order.order_id === order_id)
      ).filter(order => order !== undefined);

      setTicketingOrderRowData(uniqueOrders as TransactionReportRow[]);
      setIsLoadingTicketingOrder(false);
    } else {
      setTicketingOrderRowData([]);
      setIsLoadingTicketingOrder(false);
    }
  }, [attendeeTicketingOrderData, rfidTicketingOrderData]);

  const { deletePermission } = usePermissions("attendees");
  const navigate = useNavigate();

  const handleClone = () => {
    navigate(`/attendees/${attendee.id}/clone`);
  };

  const handleCreateCashlessAsset = () => {
    navigate(`/rfid-assets/create`, {
      state: {
        fromAttendee: true,
        attendee: {
          id: attendee.id,
          first_name: attendee.first_name,
          last_name: attendee.last_name,
          email: attendee.email,
          phone_number: attendee.phone_number,
          event_id: attendee.event_id,
          personnal_pin: attendee.personnal_pin,
        }
      }
    });
  };

  return (
      <div>
        <Form action={`/attendees/${attendee.id}/edit`} method="put">
          <div className="flex">
            <div className="ml-auto py-2">
              <TertiaryButton onClick={handleClone}>Clone Attendee</TertiaryButton>
              {deletePermission && (
                  <DeleteModal
                      onConfirm={() => {
                        fetcher.submit(
                            {},
                            {
                              method: "delete",
                              action: `/attendees/${attendee.id}/delete`,
                            }
                        );
                      }}
                  >
                    {({ setOpen }) => (
                        <SecondaryButton onClick={() => setOpen(true)}>
                          Delete
                        </SecondaryButton>
                    )}
                  </DeleteModal>
              )}
            </div>
          </div>
          <FormLayout>
            <div className="col-span-6 sm:col-span-4">
              <Input
                  label="Email"
                  name="email"
                  defaultValue={attendee?.email}
                  updateValue
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <IndeterminateCheckbox
                  name="is_suspended"
                  label="Is Suspended"
                  defaultChecked={attendee.is_suspended}
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <IndeterminateCheckbox
                  name="is_active"
                  label="Is Active"
                  defaultChecked={attendee.is_active}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="First name"
                  name="first_name"
                  defaultValue={attendee.first_name}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="Last name"
                  name="last_name"
                  defaultValue={attendee.last_name}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="Phone Number"
                  name="phone_number"
                  defaultValue={attendee.phone_number}
              />
            </div>
            <div className="col-span-6">
              <CustomSelect
                  title="Event"
                  name="event_id"
                  defaultValue={attendee.event_id}
                  options={[
                    ...events.map((event) => ({
                      label: event.name,
                      value: event.id,
                    })),
                  ]}
              />
            </div>
            <div className="col-span-6 sm:col-span-6">
              <TextArea name="note" label="Note" defaultValue={attendee.note} />
            </div>
          </FormLayout>
        </Form>
        {attendee?.optional_datas && attendee.optional_datas.length > 0 && (
          <div className="mt-2">
            <ShowLayout header="Demographics">
              <div className="px-4 py-4 sm:px-6">
                <DemographicsPanel answers={attendee.optional_datas} />
              </div>
            </ShowLayout>
          </div>
        )}
        <div className="mt-2">
          <ShowLayout
            header="Cashless Assets"
            actions={
              <div className="flex gap-2">
                <SecondaryButton onClick={() => rfidGridRef.current?.exportDataAsCsv({ fileName: "CashlessAssets.csv", columnSeparator: ",", allColumns: true })}>
                  Export to CSV
                </SecondaryButton>
                <TertiaryButton onClick={handleCreateCashlessAsset}>
                  Create Cashless Asset
                </TertiaryButton>
              </div>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingRFID ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={rfidRowData}
                        columnDefs={rfidColDefs}
                        rowHeight={rowHeight}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (rfidGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Cards On File"
            actions={
              <SecondaryButton onClick={() => cardGridRef.current?.exportDataAsCsv({ fileName: "CardsOnFile.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingCard ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={cardRowData}
                        columnDefs={cardsOnFileColDefs}
                        rowHeight={rowHeight}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (cardGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Point-of-Sale Orders"
            actions={
              <SecondaryButton onClick={() => orderGridRef.current?.exportDataAsCsv({ fileName: "PointOfSaleOrders.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingOrder ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={orderRowData}
                        columnDefs={ordersColDefs}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (orderGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Ticketing Orders"
            actions={
              <SecondaryButton onClick={() => ticketingOrderGridRef.current?.exportDataAsCsv({ fileName: "TicketingOrders.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingTicketingOrder ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={ticketingOrderRowData}
                        columnDefs={ticketingOrdersColDefs}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (ticketingOrderGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Promo Codes"
            actions={
              <SecondaryButton onClick={() => promoCodeGridRef.current?.exportDataAsCsv({ fileName: "PromoCodes.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingPromoCodes ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={promoCodeRowData}
                        columnDefs={promoCodesColDefs}
                        rowHeight={rowHeight}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (promoCodeGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
      </div>
  );
};

function DemographicsPanel({ answers }: { answers: DemographicAnswer[] }) {
  return (
    <div className="space-y-4">
      {answers.map((item, idx) => {
        const answerText = Array.isArray(item.answer)
          ? item.answer.join(", ")
          : item.answer || "No response";

        return (
          <div key={item.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">
                Question-{idx + 1}
              </span>
              <span className="text-xs text-gray-400 capitalize">{item.type}</span>
            </div>
            <p className="text-sm font-medium text-gray-700 mb-2">{item.question}</p>
            {item.options && item.options.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {item.options.map((opt, optIdx) => {
                  const isSelected = Array.isArray(item.answer)
                    ? item.answer.includes(opt)
                    : item.answer === opt;
                  return (
                    <span
                      key={optIdx}
                      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                        isSelected
                          ? "bg-primary-100 text-primary-700 ring-1 ring-primary-300"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {opt}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="mt-1">
              <span className="text-sm text-gray-500">Response: </span>
              <span className={`text-sm font-medium ${!item.answer ? "text-gray-400 italic" : "text-gray-900"}`}>
                {answerText}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
